<template>
  <div
    class="selection-tab"
    :class="{
      'selection-tab--button': url,
      'selection-tab--colour-swatch': isColour,
      [$class('disabled')]: disabled || visuallyDisabled,
      [$class('loading')]: loading,
    }"
  >
    <a
      v-if="url"
      class="
        selection-tab__label
        text-body-s-regular
      "
      :href="url"
      :title="title"
      v-html="label"
    />

    <template v-else>
      <input
        :id="id"
        v-model="localModelValue"
        class="selection-tab__input visually-hidden"
        :class="{
          [$class('disabled')]: disabled || visuallyDisabled,
          [$class('loading')]: loading,
        }"
        :disabled="disabled || loading"
        :name="name"
        type="radio"
        :value="value"
        @change="updateVModel(value)"
      >

      <label
        v-if="isColour"
        class="selection-tab__label"
        :for="id"
        :title="title"
        :style="`--swatch-colour: ${hexCode};`"
      >
        <span
          class="visually-hidden"
          v-html="label"
        />
      </label>

      <label
        v-else
        class="
          selection-tab__label
          text-body-s-regular
        "
        :for="id"
        :title="title"
        v-html="label"
      />
    </template>

    <icon-bell
      v-if="bell"
      class="selection-tab__bell"
    />
  </div>
</template>

<script>

/**
 * Vue: Selection tab (selection-tab)
 * -----------------------------------------------------------------------------
 * Simple tab button with active, disabled, and initial states.
 *
 * @param {Boolean} [bell] - Display bell icon.
 * @param {Boolean} [disabled] - Tab is disabled.
 * @param {String} [id] - ID for input, required for input.
 * @param {String} label - Tab label.
 * @param {Boolean} [loading] - Loading state.
 * @param {*} modelValue - Current value provided by v-model. This
 * is required when using `input` element (when no `url` prop is passed).
 * @param {String} [name] - Radio input group name, required for input.
 * @param {String} [title] - Title attribute.
 * @param {String} [url] - URL to navigate to when clicked, replaces input.
 * @param {*} [value] - Radio input value, required for input.
 * @param {Boolean} [visuallyDisabled] - Tab is visually disabled but can still
 * be selected.
 *
 * @emits update:model-value - Emit v-model update event.
 *
 */
import IconBell from '~icons/general/bell.svg'

export default {
  name: 'SelectionTab',

  components: {
    IconBell,
  },

  props: {
    bell: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [Number, String],
      default: '',
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [Number, String, Object],
      default: '',
    },
    name: {
      type: [Number, String],
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    value: {
      type: [Number, String, Object],
      default: '',
    },
    url: {
      type: String,
      default: '',
    },
    visuallyDisabled: {
      type: Boolean,
      default: false,
    },
    isColour: {
      type: Boolean,
      default: false,
    },
    hexCode: {
      type: String,
      default: '',
    },
  },

  emits: {
    'update:model-value': () => true,
  },

  data() {
    return {
      localModelValue: '',
    }
  },

  watch: {

    /**
     * Watch v-model value to update local value.
     */
    modelValue() {
      this.localModelValue = this.modelValue
    },
  },

  mounted() {
    this.localModelValue = this.modelValue
  },

  methods: {

    /**
     * Emit update v-model event.
     * @param {Number|String} value - Update value.
     */
    updateVModel(value) {
      this.$emit('update:model-value', value)
    },
  },
}
</script>

<style lang="scss">
@import './selection-tab';
</style>
